import {
  Box,
  Button,
  Grid,
  Stack,
  type SelectChangeEvent,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import ReportsIcon from "src/assets/svg-icons/side-bar/reports.svg";
import { useTranslation } from "react-i18next";
import youtubeIcon from "src/assets/svg-icons/aggregators/youtube.svg";
import { specificBranchHasPermission } from "src/utils";
import { useGetBranches } from "src/api/generic";
import { REPORT_BRANCH_MULTISELECT_ALL_SENTINEL } from "src/constants/reportBranchSelect";
import ReportCard from "./report-card";
import SectionHeader from "src/shared/components/group-title";
import { ReactComponent as StatementIcon } from "src/assets/svg-icons/reports/statement.svg";
import { ReactComponent as BoxesIcon } from "src/assets/svg-icons/reports/boxes.svg";
import { ReactComponent as CalcIcon } from "src/assets/svg-icons/reports/calc.svg";
import { ReactComponent as CartIcon } from "src/assets/svg-icons/reports/cart.svg";
import { ReactComponent as DeliveryIcon } from "src/assets/svg-icons/reports/delivery.svg";
import { ReactComponent as OperationIcon } from "src/assets/svg-icons/reports/operating.svg";
import { ReactComponent as SalesIcon } from "src/assets/svg-icons/side-bar/sales.svg";

const ReportsPages = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("reports");
  const { t: T } = useTranslation("general");
  const { t: purchasingT } = useTranslation("purchasing");

  const [dateRange, setDateRange] = useState<{
    startDate: string;
    endDate: string;
  }>({
    startDate: moment().startOf("month").format("yyyy-MM-DD"),
    endDate: moment().endOf("month").format("yyyy-MM-DD"),
  });

  const [branches, setBranches] = useState<number[]>([]);

  // APIS
  const { data: branchesData } = useGetBranches();

  const handleViewPLReport = () => {
    const params = new URLSearchParams({
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
    });
    if (branches.length === 1) {
      params.set("branch_id", String(branches[0]));
    }
    navigate(`/reports/profit-and-loss?${params.toString()}`);
  };

  const handleViewItemMovementReport = () => {
    const params = new URLSearchParams({
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
    });
    branches.forEach((id) => params.append("branches", String(id)));
    navigate(`/reports/item-movement?${params.toString()}`);
  };

  const handleViewOrderSalesReport = () => {
    const params = new URLSearchParams({
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
    });
    branches.forEach((id) => params.append("branches", String(id)));
    navigate(`/reports/order-sales?${params.toString()}`);
  };

  const handleViewItemDetailReport = () => {
    const params = new URLSearchParams({
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
    });
    navigate(`/reports/item-detail?${params.toString()}`);
  };

  const handleViewAggregatorStatementReport = () => {
    const params = new URLSearchParams({
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
    });
    branches.forEach((id) => params.append("branches", String(id)));
    navigate(`/reports/aggregator-statement?${params.toString()}`);
  };

  const handleViewPaymentMovementReport = () => {
    const params = new URLSearchParams({
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
    });
    branches.forEach((id) => params.append("branches", String(id)));
    navigate(`/reports/payment-movement?${params.toString()}`);
  };

  const handleViewTopItemsFluctuations = () => {
    const params = new URLSearchParams({
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
    });
    navigate(`/reports/top-items-fluctuations?${params.toString()}`);
  };

  const handleViewTaxReport = () => {
    const params = new URLSearchParams({
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
    });
    branches.forEach((id) => params.append("branches", String(id)));
    navigate(`/reports/tax?${params.toString()}`);
  };

  const handleViewAggregatorsPlReport = () => {
    const params = new URLSearchParams({
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
    });
    branches.forEach((id) => params.append("branches", String(id)));
    navigate(`/reports/aggregators-pl?${params.toString()}`);
  };

  const handleViewOperationExpensesReport = () => {
    const params = new URLSearchParams({
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
    });
    branches.forEach((id) => params.append("branches", String(id)));
    navigate(`/reports/operation-expenses?${params.toString()}`);
  };

  const handleViewAccountStatement = () => {
    const params = new URLSearchParams({
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
    });
    navigate(`/reports/account-statement?${params.toString()}`);
  };

  const handleViewSupplierStatement = () => {
    const params = new URLSearchParams({
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
    });
    navigate(`/reports/supplier-statement?${params.toString()}`);
  };

  const handleViewVendorStatement = () => {
    const params = new URLSearchParams({
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
    });
    navigate(`/reports/vendor-statement?${params.toString()}`);
  };

  const handleViewExpensesStatement = () => {
    const params = new URLSearchParams({
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
    });
    navigate(`/reports/expenses-statement?${params.toString()}`);
  };

  const handleViewTrialBalance = () => {
    const params = new URLSearchParams({
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
    });
    navigate(`/reports/trial-balance?${params.toString()}`);
  };

  const handleViewGeneralLedger = () => {
    const params = new URLSearchParams({
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
    });
    navigate(`/reports/general-ledger?${params.toString()}`);
  };

  const handleViewBalanceSheet = () => {
    navigate(`/reports/balance-sheet`);
  };

  const branchesHavePermission =
    branchesData?.data.filter((branch) =>
      specificBranchHasPermission("download_third_eye_report", branch.id),
    ) || [];

  return (
    <Box gap={2} p={{ xs: 2, sm: "8px 32px 32px 32px" }}>
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        gap={2}
        flexWrap={"wrap"}
        mb={3}
      >
        <Stack
          direction={"row"}
          spacing={"12px"}
          alignItems={"center"}
          color={"#101828"}
        >
          <Box
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            width={"60px"}
            height={"60px"}
            bgcolor={"#D0D5DD"}
            borderRadius={"16px"}
          >
            <img src={ReportsIcon} width={"32px"} alt="" />
          </Box>
          <Box>
            <Typography
              fontSize={"32px"}
              lineHeight={"38.4px"}
              color={"#101828"}
            >
              {t("thirdEyeReport")}
            </Typography>
            <Typography fontSize={"20px"} lineHeight={"24px"} color={"#98A2B3"}>
              {t("listOfChosenReports")}
            </Typography>
          </Box>
        </Stack>

        <Button
          variant="outlined"
          color="error"
          sx={{ height: "48px", border: "1px solid  #B32318" }}
          startIcon={<img src={youtubeIcon} alt="" width={"34px"} />}
          href="https://www.youtube.com/watch?v=uxQmpaBLMpc"
          target="_blank"
        >
          {T("viewHelp")}
        </Button>
      </Box>

      <Grid container spacing={2} mt={2}>
        <Grid item xs={12} mb={1}>
          <SectionHeader
            title={t("financialStatements")}
            icon={<StatementIcon />}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <ReportCard
            title={t("PLReport")}
            description="Description"
            onView={handleViewPLReport}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <ReportCard
            title={`${T("new")} ${t("balanceSheet")}`}
            description="Description"
            onView={handleViewBalanceSheet}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <ReportCard
            title={`${T("new")} ${t("trialBalance")}`}
            description="Description"
            onView={handleViewTrialBalance}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <ReportCard
            title={t("generalLedgerReport")}
            description="Description"
            onView={handleViewGeneralLedger}
          />
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{
              p: 3,
              height: 120,
              border: "1px solid",
              borderColor: "#EAECF0",
              borderRadius: "20px",
              bgcolor: "#FFF",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              opacity: 0.75,
              cursor: "not-allowed",
              userSelect: "none",
              my: 3,
            }}
          >
            <Typography variant="h6" fontWeight={700} color="text.secondary">
              Forecasting Report
            </Typography>

            <Box
              sx={{
                px: 1.5,
                py: 0.25,
                borderRadius: "999px",
                bgcolor: "#F2F4F7",
              }}
            >
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
              >
                {t("soon")}
              </Typography>
            </Box>
          </Box>
        </Grid>
        {/* delivery apps reports       */}
        <Grid item xs={12} mb={1}>
          <SectionHeader title={t("deliveryApps")} icon={<DeliveryIcon />} />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <ReportCard
            title={t("aggsReport")}
            description={t("aggsReport")}
            onView={handleViewAggregatorsPlReport}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <ReportCard
            title={t("aggsStatementReport")}
            description={t("aggsStatementReport")}
            onView={handleViewAggregatorStatementReport}
          />
        </Grid>
        {/* sales reports */}
        <Grid item xs={12} mb={1} mt={3}>
          <SectionHeader title={T("sales")} icon={<SalesIcon />} />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <ReportCard
            title={t("orderSalesReport")}
            description={t("orderSalesReportDescription")}
            onView={handleViewOrderSalesReport}
          />
        </Grid>
        {/* accounting reports       */}
        <Grid item xs={12} mb={1} mt={3}>
          <SectionHeader title={t("accounting")} icon={<CalcIcon />} />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <ReportCard
            title={t("accountStatement")}
            description={t("accountStatementDescription")}
            onView={handleViewAccountStatement}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <ReportCard
            title={t("paymentTransactions")}
            description={t("paymentTransactionsDescription")}
            onView={handleViewPaymentMovementReport}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <ReportCard
            title={t("taxReport")}
            description={t("taxReport")}
            onView={handleViewTaxReport}
          />
        </Grid>
        {/* operation expenses reports */}
        <Grid item xs={12} mb={1} mt={3}>
          <SectionHeader
            title={T("operationExpenses")}
            icon={<OperationIcon />}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <ReportCard
            title={t("expensesStatementReport")}
            description={t("expensesReportDescription")}
            onView={handleViewExpensesStatement}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <ReportCard
            title={t("vendorStatementReport")}
            description={t("vendorReportDescription")}
            onView={handleViewVendorStatement}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <ReportCard
            title={t("OCReport")}
            description={t("operationCostReportDescription")}
            onView={handleViewOperationExpensesReport}
          />
        </Grid>
        {/* purchasing reports       */}
        <Grid item xs={12} mb={1} mt={3}>
          <SectionHeader title={T("purchasing")} icon={<CartIcon />} />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <ReportCard
            title={t("purchaseItemsReport")}
            description={t("purchaseItemsReportDescription")}
            onView={handleViewItemDetailReport}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <ReportCard
            title={t("supplierReport")}
            description={t("supplierReportDescription")}
            onView={handleViewSupplierStatement}
          />
        </Grid>
        {/* inventory reports */}
        <Grid item xs={12} mb={1} mt={3}>
          <SectionHeader title={T("inventory")} icon={<BoxesIcon />} />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <ReportCard
            title={t("ItemMovementReport")}
            description={t("itemMovementReportDescription")}
            onView={handleViewItemMovementReport}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <ReportCard
            title={purchasingT("topItemsFluctuations")}
            description={purchasingT("topItemsFluctuations")}
            onView={handleViewTopItemsFluctuations}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ReportsPages;
